/**
 * Quantis Voice call state for the signed-in user, shared by web and native.
 * Each platform hands in its API transport and its audio engine; this owns
 * the access-token lifecycle, the one live call, the ringing call and the
 * counter that tells history and voicemail lists to reload.
 */
import React from 'react';
import type { VoiceApi, VoiceConfig } from '../../services/VoiceService';
import { normaliseDialled } from './voiceFormat';
import type { VoiceCallState, VoiceEngine } from './voiceEngine';

export type VoiceStatus = 'off' | 'connecting' | 'ready' | 'offline';

export interface VoiceCall {
  id: string;
  direction: 'inbound' | 'outbound';
  state: VoiceCallState;
  number: string;
  name: string | null;
  connectedAt: number | null;
  muted: boolean;
}

export interface IncomingVoiceCall { id: string; number: string; name: string | null; lineName: string | null }

export interface VoiceContextValue {
  config: VoiceConfig | null;
  status: VoiceStatus;
  /** False where this device has no audio engine yet (iPhone and iPad for now). */
  canCall: boolean;
  call: VoiceCall | null;
  incoming: IncomingVoiceCall | null;
  error: string | null;
  playingVoicemailId: string | null;
  /** Bumps when a call ends or a voicemail is heard, so lists reload. */
  historyVersion: number;
  api: VoiceApi;
  dial: (raw: string, name?: string | null) => boolean;
  accept: () => void;
  decline: () => void;
  hangup: () => void;
  toggleMute: () => void;
  sendDigits: (digits: string) => void;
  playVoicemail: (id: string) => Promise<void>;
  stopVoicemail: () => void;
  clearError: () => void;
  /** Reads a prompt aloud, so an admin can hear the wording before saving it. */
  previewSpeech: (text: string) => void;
  /** Asks the server again, after an admin sets up the line. */
  reloadConfig: () => void;
}

const VoiceContext = React.createContext<VoiceContextValue | null>(null);

export function useQuantisVoice(): VoiceContextValue | null {
  return React.useContext(VoiceContext);
}

const TOKEN_REFRESH_MS = 50 * 60_000;

export function VoiceProvider({ api, engine, children }: { api: VoiceApi; engine: VoiceEngine | null; children: React.ReactNode }) {
  const [config, setConfig] = React.useState<VoiceConfig | null>(null);
  const [status, setStatus] = React.useState<VoiceStatus>('off');
  const [call, setCall] = React.useState<VoiceCall | null>(null);
  const [incoming, setIncoming] = React.useState<IncomingVoiceCall | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [playingVoicemailId, setPlaying] = React.useState<string | null>(null);
  const [historyVersion, setHistoryVersion] = React.useState(0);
  // Read inside engine events without re-subscribing on every change.
  const incomingRef = React.useRef<IncomingVoiceCall | null>(null);
  const dialledRef = React.useRef<{ number: string; name: string | null } | null>(null);

  const [configVersion, setConfigVersion] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    api.config().then((next) => { if (!cancelled) setConfig(next); }, () => { if (!cancelled) setConfig({ enabled: false }); });
    return () => { cancelled = true; };
  }, [api, configVersion]);

  const enabled = config?.enabled === true && config.connected === true && engine !== null;

  React.useEffect(() => {
    if (!enabled || !engine) return;
    let cancelled = false;
    // Until a start has gone through, every refresh is a start: a failed first
    // token would otherwise leave the timer sending updates to nothing.
    let started = false;
    const refresh = async (first: boolean) => {
      try {
        const { token } = await api.token();
        if (cancelled) return;
        engine.send(first || !started ? { type: 'start', token } : { type: 'updateToken', token });
        started = true;
      } catch {
        if (!cancelled) setStatus('offline');
      }
    };

    const unsubscribe = engine.subscribe((event) => {
      switch (event.type) {
        case 'ready':
          // The engine page reloaded: any call it held is gone.
          incomingRef.current = null;
          setIncoming(null);
          setCall(null);
          setStatus('connecting');
          void refresh(true);
          break;
        case 'registered': setStatus('ready'); break;
        case 'unregistered': setStatus('offline'); break;
        case 'tokenWillExpire': void refresh(false); break;
        case 'incoming': {
          const next = { id: event.callId, number: event.from, name: event.callerName, lineName: event.lineName ?? null };
          incomingRef.current = next;
          setIncoming(next);
          break;
        }
        case 'state': {
          if (event.state === 'closed') {
            if (incomingRef.current?.id === event.callId) { incomingRef.current = null; setIncoming(null); }
            setCall((current) => (current && (current.id === event.callId || event.direction === 'outbound') ? null : current));
            setHistoryVersion((v) => v + 1);
            break;
          }
          setCall((current) => {
            const ringing = incomingRef.current?.id === event.callId ? incomingRef.current : null;
            const base: VoiceCall = current ?? {
              id: event.callId,
              direction: event.direction,
              state: event.state,
              number: ringing?.number ?? dialledRef.current?.number ?? '',
              name: ringing?.name ?? dialledRef.current?.name ?? null,
              connectedAt: null,
              muted: false,
            };
            return { ...base, id: event.callId, state: event.state, connectedAt: event.state === 'open' ? base.connectedAt ?? Date.now() : base.connectedAt };
          });
          if (event.state === 'open' && incomingRef.current?.id === event.callId) { incomingRef.current = null; setIncoming(null); }
          break;
        }
        case 'muted': setCall((current) => (current ? { ...current, muted: event.muted } : current)); break;
        case 'playback': if (!event.playing) setPlaying(null); break;
        case 'error': setError(event.message); break;
      }
    });

    // The browser engine is ready at once; a web view says so when its page loads.
    setStatus('connecting');
    void refresh(true);
    const timer = setInterval(() => void refresh(false), TOKEN_REFRESH_MS);
    // Retry sooner while offline, rather than waiting out the refresh interval.
    const retry = setInterval(() => { if (!started) void refresh(true); }, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearInterval(retry);
      unsubscribe();
      engine.send({ type: 'stop' });
      setStatus('off');
    };
  }, [enabled, engine, api]);

  const value = React.useMemo<VoiceContextValue>(() => ({
    config,
    status,
    canCall: engine !== null,
    call,
    incoming,
    error,
    playingVoicemailId,
    historyVersion,
    api,
    dial: (raw, name = null) => {
      const number = normaliseDialled(raw);
      if (!number) { setError('Enter a full UK phone number, including the area code.'); return false; }
      if (call || incoming) { setError('Finish the current call first.'); return false; }
      if (!engine || status !== 'ready') { setError('Quantis Voice is still connecting. Try again in a moment.'); return false; }
      dialledRef.current = { number, name };
      setCall({ id: 'pending', direction: 'outbound', state: 'connecting', number, name, connectedAt: null, muted: false });
      engine.send({ type: 'call', to: number });
      return true;
    },
    accept: () => { if (incoming) engine?.send({ type: 'accept', callId: incoming.id }); },
    decline: () => {
      if (!incoming) return;
      engine?.send({ type: 'reject', callId: incoming.id });
      incomingRef.current = null;
      setIncoming(null);
    },
    hangup: () => { engine?.send({ type: 'hangup' }); setCall(null); },
    toggleMute: () => { if (call) engine?.send({ type: 'mute', muted: !call.muted }); },
    sendDigits: (digits) => engine?.send({ type: 'digits', digits }),
    playVoicemail: async (id) => {
      try {
        const url = await api.voicemailAudioUrl(id);
        engine?.send({ type: 'play', url });
        setPlaying(id);
        await api.markHeard(id).then(() => setHistoryVersion((v) => v + 1), () => {});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The voicemail could not be opened');
      }
    },
    stopVoicemail: () => { engine?.send({ type: 'stopPlayback' }); setPlaying(null); },
    clearError: () => setError(null),
    previewSpeech: (text) => {
      if (engine) { engine.send({ type: 'speak', text }); return; }
      const synth = (globalThis as { speechSynthesis?: { speak: (u: unknown) => void; cancel: () => void } }).speechSynthesis;
      const Utterance = (globalThis as { SpeechSynthesisUtterance?: new (text: string) => { lang: string } }).SpeechSynthesisUtterance;
      if (!synth || !Utterance) { setError('Previews play on Quantis for the web and the Mac.'); return; }
      const utterance = new Utterance(text);
      utterance.lang = 'en-GB';
      synth.cancel();
      synth.speak(utterance);
    },
    reloadConfig: () => setConfigVersion((v) => v + 1),
  }), [config, status, call, incoming, error, playingVoicemailId, historyVersion, api, engine]);

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}
